import React from 'react'
import PropTypes from 'prop-types'
import {useParams} from 'react-router-dom'
import loadable from '@loadable/component'
import {useCategory} from '@salesforce/commerce-sdk-react'
import {HTTPError} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import LoadingSpinner from '../../components/loading-spinner'
import {MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '../../constants'
import LenisProvider from '../../contexts/lenis'
import CmsModules from '../../leap/modules'
import Seo from '../../components/seo'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import {useContentAsset} from '../../hooks/useScapiCustomApi'
import {PageTheme} from '../../leap/theme'
import {HeaderVariant} from '../../leap/components/navigation/header/header-variant'
import {getHeaderBgFromFirstModule} from '../../leap/modules/get-header-bg-from-first-module'
import {useChakraContext} from '@chakra-ui/react3'

const PageNotFound = loadable(() => import('../page-not-found'))

const ProductExplore = ({categoryId: categoryIdOverride}) => {
    const params = useParams()

    const categoryId = categoryIdOverride || params.categoryId
    const system = useChakraContext()

    useInitialDataLayer({pageType: 'Product Explore'})

    const {
        error: categoryError,
        data: category,
        isLoading
    } = useCategory(
        {
            parameters: {
                id: categoryId,
                levels: 0
            }
        },
        {
            enabled: categoryId != null && categoryId !== ''
        }
    )

    // pre-fetched so the first module's background can set the header background
    const {data: modules} = useContentAsset(
        {parameters: {assetId: category?.c_velocityAssetID}},
        {enabled: Boolean(category?.c_velocityAssetID)}
    )

    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        res.set(
            'Cache-Control',
            cacheConfig?.pep ||
                `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    const categoryErrorStatus = categoryError?.response?.status
    switch (categoryErrorStatus) {
        case undefined:
            // No Error.
            break
        case 404:
            return <PageNotFound {...params} />
        default:
            throw new HTTPError(categoryErrorStatus, `HTTP Error ${categoryErrorStatus} occurred.`)
    }

    if (isLoading) {
        return <LoadingSpinner />
    }

    if (!category.c_velocityAssetID) {
        return <PageNotFound {...params} />
    }

    return (
        <>
            <PageTheme theme={category.c_colorTheme} />
            <HeaderVariant
                variant="floating"
                bg={getHeaderBgFromFirstModule(system, modules) ?? 'background.section.subtle'}
            />
            <LenisProvider>
                <Seo
                    title={category?.pageTitle}
                    description={category?.pageDescription}
                    keywords={category?.pageKeywords}
                />
                <CmsModules assetId={category.c_velocityAssetID} modules={modules} />
            </LenisProvider>
        </>
    )
}

ProductExplore.propTypes = {
    categoryId: PropTypes.string
}

export default ProductExplore
